import { API_BASE_URL } from '../config/env';

const CONTACT_NAME_CACHE_KEY = 'crm_cw_contact_names_v1';
const CONTACT_NAME_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export function normalizeDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

function toTitleCaseWord(word) {
  const safe = String(word || '').trim();
  if (!safe) return '';
  const lower = safe.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function toTitleCaseName(text) {
  return String(text || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(toTitleCaseWord)
    .join(' ');
}

export function sanitizeDisplayName(value, fallback = 'Sin nombre') {
  const trimmed = String(value || '').trim();
  if (!trimmed) return fallback;

  const compact = trimmed.replace(/\s+/g, ' ');
  const alnum = compact
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '');

  if (!alnum || alnum.length < 2) return fallback;
  return compact;
}

export function getChatwootSenderName(conversation) {
  return conversation?.meta?.sender?.name || conversation?.sender?.name || '';
}

function formatPersonDisplayName({ apellido = '', nombre = '', raw = '' } = {}) {
  const a = String(apellido || '').trim();
  const n = String(nombre || '').trim();
  if (n && a) return toTitleCaseName(`${n} ${a}`);

  const source = String(raw || n || a).trim();
  if (!source) return '';

  if (source.includes(',')) {
    const parts = source
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length >= 2) {
      const [first, ...rest] = parts;
      return toTitleCaseName(`${rest.join(' ')} ${first}`);
    }
  }

  return toTitleCaseName(source);
}

function readContactNameCache() {
  try {
    const raw = localStorage.getItem(CONTACT_NAME_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function getCachedContactDisplayName(phone) {
  const key = normalizeDigits(phone);
  if (!key) return '';
  const entry = readContactNameCache()[key];
  if (!entry?.name) return '';
  if (Date.now() - Number(entry.at || 0) > CONTACT_NAME_CACHE_TTL_MS) return '';
  return String(entry.name).trim();
}

export function persistContactDisplayName(phone, displayName) {
  const key = normalizeDigits(phone);
  const name = String(displayName || '').trim();
  if (!key || !name) return;
  try {
    const cache = readContactNameCache();
    cache[key] = { name, at: Date.now() };
    localStorage.setItem(CONTACT_NAME_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // fallback silencioso
  }
}

/** API Farmachat / bot atención: "NOMBRE(S), APELLIDO" → "Nombre Apellido". */
export function formatFarmachatClientNombre(raw) {
  const source = String(raw || '').trim();
  if (!source) return '';
  if (source.includes(',')) {
    const [nombrePart, apellido] = source.split(',', 2).map((part) => part.trim());
    if (nombrePart && apellido) return toTitleCaseName(`${nombrePart} ${apellido}`);
    if (nombrePart) return toTitleCaseName(nombrePart);
    if (apellido) return toTitleCaseName(apellido);
  }
  return toTitleCaseName(source);
}

export function isClientIdentityResolved(session) {
  const phase = String(session?.identity_phase || '').trim().toLowerCase();
  const client = session?.client_identity;
  if (!client || typeof client !== 'object') return false;
  if (phase !== 'identified') return false;
  return Boolean(String(client.nombre || client.nombre_apellido || '').trim());
}

export function resolveSimClientDisplayName({
  profile,
  sessionClientIdentity,
  phone,
  session,
  identityResolved
}) {
  const resolved =
    typeof identityResolved === 'boolean'
      ? identityResolved
      : isClientIdentityResolved(session);

  if (resolved) {
    const fromSession = formatFarmachatClientNombre(sessionClientIdentity?.nombre);
    if (fromSession) return sanitizeDisplayName(fromSession, '');
  }

  if (!resolved) return '';

  const fromDb = getClientDisplayNameFromProfile(profile);
  if (fromDb) return fromDb;

  const fromCache = getCachedContactDisplayName(phone);
  if (fromCache) return sanitizeDisplayName(fromCache, '');

  return '';
}

function formatNombrePlexDisplay(raw) {
  const source = String(raw || '').trim();
  if (!source) return '';

  if (source.includes(',')) {
    const [beforeComma, afterComma] = source.split(',', 2).map((part) => part.trim());
    // Farmachat: "CARLOS EMILIANO, GARCIA" — nombre compuesto antes de la coma.
    if (beforeComma && afterComma && beforeComma.includes(' ')) {
      return formatFarmachatClientNombre(source);
    }
  }

  return formatPersonDisplayName({ raw: source });
}

export function getClientDisplayNameFromProfile(profile) {
  if (!profile) return '';
  const apellido = String(profile.apellido_geclisa || '').trim();
  const nombre = String(profile.nombre_geclisa || '').trim();
  const fromGeclisa = formatPersonDisplayName({ apellido, nombre });
  if (fromGeclisa) return sanitizeDisplayName(fromGeclisa, '');

  const nombrePlex = String(profile.nombre_plex || '').trim();
  const fromPlex = formatNombrePlexDisplay(nombrePlex);
  return sanitizeDisplayName(fromPlex, '');
}

export function formatWhatsappPhone(phoneRaw) {
  const digits = normalizeDigits(phoneRaw);
  if (!digits) return '';

  let local = digits;
  if (local.startsWith('549') && local.length >= 12) local = local.slice(3);
  else if (local.startsWith('54') && local.length >= 11) local = local.slice(2);

  if (local.length === 10) {
    return `+54 ${local.slice(0, 3)} ${local.slice(3, 6)}-${local.slice(6)}`;
  }
  if (local.length === 8) {
    return `+54 261 ${local.slice(0, 3)}-${local.slice(3)}`;
  }
  return `+${digits}`;
}

export function getDisplayInitials(name) {
  const n = sanitizeDisplayName(name, '').trim();
  if (!n || n === 'Sin nombre') return 'WA';
  const parts = n.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  return n.slice(0, 2).toUpperCase();
}

export function resolveConversationDisplayLabel({ conversation, profile = null, cachedDisplayName = '' }) {
  const fromDb = getClientDisplayNameFromProfile(profile);
  if (fromDb) return fromDb;

  const cached = String(cachedDisplayName || '').trim();
  if (cached) return cached;

  const fromPhoneCache = getCachedContactDisplayName(conversation?.sender?.phone_number);
  if (fromPhoneCache) return sanitizeDisplayName(fromPhoneCache, '');

  const whatsapp = sanitizeDisplayName(getChatwootSenderName(conversation), '');
  if (whatsapp) return toTitleCaseName(whatsapp);

  const phoneLabel = formatWhatsappPhone(conversation?.sender?.phone_number);
  if (phoneLabel) return phoneLabel;

  return 'Contacto WhatsApp';
}

function isIncomingMessage(message) {
  const type = message?.message_type;
  if (type === 0 || type === 'incoming') return true;
  if (typeof type === 'string' && type.trim() === '0') return true;
  return false;
}

function isActivityMessage(message) {
  const type = message?.message_type;
  if (type === 2 || type === 'activity') return true;
  if (typeof type === 'string' && type.trim() === '2') return true;

  const content = String(message?.content || '').trim();
  if (!content) return false;
  const low = content.toLowerCase();

  return (
    /conversaci[oó]n\s+(fue\s+)?(marcad|asignad|reabier|cerrad|eliminad|pospuest|resuelt)/i.test(content) ||
    /conversation\s+was\s+(marked|assigned|reopened|resolved|snoozed|deleted)/i.test(low)
  );
}

function parseQuotedReply(content) {
  const raw = String(content || '');
  const lines = raw.split('\n');
  if (!lines.length || !lines[0].startsWith('> ')) {
    return { quoted: '', text: raw };
  }

  const quotedLines = [];
  let i = 0;
  while (i < lines.length && lines[i].startsWith('> ')) {
    quotedLines.push(lines[i].slice(2));
    i += 1;
  }

  while (i < lines.length && lines[i].trim() === '') i += 1;
  return { quoted: quotedLines.join('\n').trim(), text: lines.slice(i).join('\n').trim() || raw };
}

function stripMarkdown(text) {
  return String(text || '')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

export function buildConversationMessagePreview(conversation) {
  const message = conversation?.last_message;
  const content = message?.content;

  if (message && isActivityMessage(message)) {
    const short = stripMarkdown(message?.content);
    return short.length > 90 ? `${short.slice(0, 87)}…` : short;
  }

  const parsed = parseQuotedReply(content);
  let text = stripMarkdown(parsed.text || parsed.quoted || '');
  if (text) {
    return text.length > 110 ? `${text.slice(0, 107)}…` : text;
  }

  const attachments = [
    ...(Array.isArray(message?.attachments) ? message.attachments : []),
    ...(Array.isArray(message?.content_attributes?.attachments) ? message.content_attributes.attachments : [])
  ];

  if (attachments.length) {
    const type = String(attachments[0]?.file_type || attachments[0]?.type || '').toLowerCase();
    if (type.includes('audio')) return '🎤 Audio';
    if (type.includes('image')) return '📷 Imagen';
    if (type.includes('video')) return '🎬 Video';
    if (type.includes('pdf')) return '📄 PDF';
    return '📎 Adjunto';
  }

  if (message && !isIncomingMessage(message)) return '💬 Respuesta del operador';
  return '💬 Nuevo mensaje del paciente';
}

export function buildUnreadFabPreviewItem(conversation) {
  if (!conversation || Number(conversation?.unread_count || 0) <= 0) return null;

  const cachedDisplayName = getCachedContactDisplayName(conversation?.sender?.phone_number);
  const title = resolveConversationDisplayLabel({ conversation, cachedDisplayName });
  const phone = formatWhatsappPhone(conversation?.sender?.phone_number);
  const subtitle = phone && title !== phone ? phone : '';
  const body = buildConversationMessagePreview(conversation);
  const initials = getDisplayInitials(title);

  return {
    conversationId: conversation.id,
    title,
    subtitle,
    body,
    initials,
    lastMessageId: conversation.last_message?.id || null,
    convUnread: Number(conversation.unread_count || 0)
  };
}

export function buildUnreadFabPreviewList(conversations = []) {
  const unread = conversations.filter((conv) => Number(conv?.unread_count || 0) > 0);
  if (!unread.length) return [];

  return [...unread]
    .sort((a, b) => {
      const unreadDiff = Number(b.unread_count || 0) - Number(a.unread_count || 0);
      if (unreadDiff !== 0) return unreadDiff;
      const aTs = new Date(a.last_message?.created_at || a.updated_at || 0).getTime();
      const bTs = new Date(b.last_message?.created_at || b.updated_at || 0).getTime();
      return bTs - aTs;
    })
    .map((conv) => buildUnreadFabPreviewItem(conv))
    .filter(Boolean);
}

/** @deprecated Prefer buildUnreadFabPreviewList */
export function buildUnreadFabPreview(conversations = []) {
  const list = buildUnreadFabPreviewList(conversations);
  return list[0] || null;
}

export async function enrichFabPreviewListWithProfiles(items = [], conversations = []) {
  if (!items.length) return [];

  const byId = new Map(conversations.map((conv) => [conv.id, conv]));
  return Promise.all(
    items.map(async (item) => {
      const conv = byId.get(item.conversationId);
      if (!conv) return item;
      return enrichFabPreviewWithProfile(item, conv);
    })
  );
}

export async function enrichFabPreviewWithProfile(preview, conversation) {
  if (!preview || !conversation?.sender?.phone_number) return preview;

  try {
    const senderNameNorm = String(getChatwootSenderName(conversation) || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

    const res = await fetch(
      `${API_BASE_URL}/api/asociacion/by-phone?phone=${encodeURIComponent(conversation.sender.phone_number)}&name=${encodeURIComponent(senderNameNorm)}`,
      { credentials: 'include' }
    );
    if (!res.ok) return preview;

    const json = await res.json().catch(() => ({}));
    const profile = json?.data || null;
    if (!profile) return preview;

    const title = resolveConversationDisplayLabel({ conversation, profile });
    const phone = formatWhatsappPhone(conversation?.sender?.phone_number);
    const subtitle = phone && title !== phone ? phone : preview.subtitle;

    return {
      ...preview,
      title,
      subtitle,
      initials: getDisplayInitials(title)
    };
  } catch {
    return preview;
  }
}
